import { useEffect, useMemo } from "react";

import {
  selectedDeploymentHealthLocationVariable,
  useFilterByOffline,
  useSelectedDeploymentHealthLocation,
  useSelectedDeploymentHealthTagLocations,
} from "../variables/deploymentHealth";

import { AutocompleteOptionType } from "../../../common/models/autocomplete";
import { ServiceTypeEnum } from "../../model-manager/variables/modelManager";
import {
  IParsedDeploymentHealthRow,
  useDeploymentHealthLeaves,
} from "./useDeploymentHealthLeaves";

export interface IDeploymentHealthTableRow extends IParsedDeploymentHealthRow {
  locationStatus: boolean;
  nodeStatus: boolean;
  deviceStatus: boolean;
  serviceStatus: boolean;
}

interface IUseDeploymentHealthTableRows {
  data?: IDeploymentHealthTableRow[];
  loading: boolean;
  fetchingMore: boolean;
}

export const useDeploymentHealthTableRows =
  (): IUseDeploymentHealthTableRows => {
    const locationVariable = useSelectedDeploymentHealthLocation();

    const normalizedLocationName = locationVariable?.value
      ? locationVariable.value.split("#L#")[1]
      : undefined;
    const { rows, loading, fetchingMore } = useDeploymentHealthLeaves(
      1000,
      normalizedLocationName
    );
    const tagLocationsVariable = useSelectedDeploymentHealthTagLocations();

    const offlineStatus = useFilterByOffline();

    function removeGasLeakType(serviceType: string): string {
      return serviceType.replace(/Gas /g, "");
    }

    function removeSafetyAndSecurityType(serviceType: string): string {
      return serviceType.replace(
        /Safety and Security/g,
        ServiceTypeEnum.PERSONNEL_DETECTION
      );
    }

    useEffect(() => {
      return () => {
        selectedDeploymentHealthLocationVariable(null);
      };
    }, []);

    // First useMemo: Expensive status calculations (only when rows change)
    const mappedData = useMemo(() => {
      if (!rows) return [];

      return rows.map((row: IParsedDeploymentHealthRow) => {
        // not sure how we should use status now but the conditional including isExpectedOnline is strange; there is a check later to determine gray status
        const nodeStatus = row.nodes.every(
          node =>
            node.status ||
            node.isExpectedOnline === false ||
            node.assignedWatcher !== "CLEANCONNECT" // don't consider entity not watched by CC in calculation
        );
        const deviceStatus = row.devices.every(
          device =>
            device.status ||
            device.isExpectedOnline === false ||
            device.assignedWatcher !== "CLEANCONNECT" // don't consider entity not watched by CC in calculation
        );
        const serviceStatus = row.services.every(
          service =>
            service.status ||
            service.isExpectedOnline === false ||
            service.assignedWatcher !== "CLEANCONNECT" // don't consider entity not watched by CC in calculation
        );

        const locationStatus = nodeStatus && deviceStatus && serviceStatus;

        const updatedServices = row.services.map(service => {
          let serviceType = service.serviceType;

          if (service.serviceType.includes("Gas ")) {
            serviceType = removeGasLeakType(service.serviceType);
          }

          if (
            service.serviceType.includes(ServiceTypeEnum.SAFETY_AND_SECURITY)
          ) {
            serviceType = removeSafetyAndSecurityType(service.serviceType);
          }

          return {
            ...service,
            serviceType,
          };
        });

        return {
          ...row,
          services: updatedServices,
          locationStatus,
          nodeStatus,
          deviceStatus,
          serviceStatus,
        };
      });
    }, [rows]);

    // Second useMemo: Cheap filtering/sorting (when filters change)
    const data = useMemo(() => {
      // Early return: tag feature is active but no tags selected means show nothing
      if (
        !locationVariable?.value &&
        tagLocationsVariable &&
        tagLocationsVariable.length === 0
      ) {
        return [];
      }

      let items = [...mappedData];

      // Filter by location
      if (locationVariable?.value) {
        items = items.filter(
          (item): boolean => item?.locationId === locationVariable.value
        );
      }

      // Filter by tag locations (only if tags are selected)
      if (tagLocationsVariable?.length) {
        items = items.filter((item): boolean =>
          tagLocationsVariable.some(
            (tag: AutocompleteOptionType) => item?.locationId === tag?.value
          )
        );
      }

      // Filter locations with fully online status
      if (offlineStatus) {
        items = items.filter((item): boolean => !item.locationStatus);
      }

      // Sort by location status (offline first)
      return items.sort((a, b) =>
        a.locationStatus === b.locationStatus ? 0 : a.locationStatus ? 1 : -1
      );
    }, [
      mappedData,
      locationVariable?.value,
      tagLocationsVariable,
      offlineStatus,
    ]);

    return { data, loading, fetchingMore };
  };
